import { InfoCircleOutlined } from "@ant-design/icons";
import { Button, Col, Form, Row, Select } from "antd";
import { complexDigitToman, delay, validateInput } from "../../../../tools/tools";
import Iinput from "../../../auxiliary/inputs/Input";
import Loading from "../../../auxiliary/loader/loading";
import IncressCharge from "../../../auxiliary/incressCharge/charge";
import KitRequestModal from "./kitRequestModal";
import { useAPIPublic, useDealer } from "../../../../tools/api";
import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { globalValue } from "../../../../reducer/serviceReducerGlobala";

const NewRequest = ({ t }) => {
	const [form] = Form.useForm();

	const { queryPoolType, queryTaxPercent, queryCurrentUser } = useAPIPublic();
	const { queryDealerPoolMapping, queryDealerKitLimits, registerKitRequest } = useDealer();
	const dispatch = useDispatch();
	const { _currentUser } = useSelector((state) => state?.another);
	const walletAmount = Number(_currentUser?.walletAmount) || 0;
	const bankRef = useRef("");

	const [pool, setPool] = useState([]);
	const [mapPool, setMapPool] = useState([]);
	const [choosePool, setChoosePool] = useState(null);
	const [kitLimits, setKitLimits] = useState(null);
	const [tax, setTax] = useState(0);
	const [simCount, setSimCount] = useState(0);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [modalState, setModalState] = useState(null);
	const [openIncress, setOpenIncress] = useState(false);

	useEffect(() => {
		const loadService = async () => {
			try {
				const [poolTypes, dealerPools, taxPercent, dealerKitLimits] = await Promise.all([
					queryPoolType(),
					queryDealerPoolMapping(),
					queryTaxPercent(),
					queryDealerKitLimits(),
				]);

				if (!Array.isArray(poolTypes)) {
					throw new Error("Pool types response is invalid");
				}

				if (!Array.isArray(dealerPools)) {
					throw new Error("Dealer pools response is invalid");
				}

				if (!dealerKitLimits || typeof dealerKitLimits !== "object") {
					throw new Error("Dealer kit limits response is invalid");
				}

				// مخزن با poolType=5 تو این صفحه (درخواست جدید نماینده) نباید نمایش داده بشه
				const filteredPools = poolTypes.filter(
					(item) => Number(item.value) !== 5 && dealerPools.some((map) => Number(map.poolType) === Number(item.value)),
				);

				setPool(filteredPools);
				setMapPool(dealerPools);
				setKitLimits(dealerKitLimits);
				setTax(Number(taxPercent) || 0);
			} catch (error) {
				console.error("Failed to load request data:", error);

				setPool([]);
				setMapPool([]);
				setChoosePool(null);
				setKitLimits(null);
				setTax(0);
				setSimCount(0);
			}
		};

		loadService();
	}, []);

	const selectedLimits = useMemo(() => {
		const poolType = Number(choosePool?.poolType);

		if (!kitLimits || !poolType) {
			return {
				min: 0,
				max: 0,
			};
		}

		if (poolType === 1) {
			return {
				min: Number(kitLimits.minPickupQty) || 0,
				max: Number(kitLimits.maxPickupQty) || 0,
			};
		}

		if (poolType === 2) {
			return {
				min: Number(kitLimits.minSaleSwapQty) || 0,
				max: Number(kitLimits.maxSaleSwapQty) || 0,
			};
		}

		return {
			min: 0,
			max: 0,
		};
	}, [choosePool, kitLimits]);

	const handlePoolChange = (value) => {
		const selectedPool = mapPool.find((item) => Number(item.poolType) === Number(value));

		setChoosePool(selectedPool ?? null);
		setSimCount(0);

		form.setFieldValue("certno", undefined);
		form.validateFields(["certno"]).catch(() => {});
	};

	const handlePoolClear = () => {
		setChoosePool(null);
		setSimCount(0);

		form.setFieldsValue({
			type: undefined,
			certno: undefined,
		});
	};

	const handleCountChange = (event) => {
		const rawValue = event?.target?.value ?? event;

		const normalizedValue = String(rawValue ?? "").replace(/[^\d]/g, "");

		const count = Number(normalizedValue);

		setSimCount(Number.isFinite(count) ? count : 0);
	};

	const isCountValid = useMemo(() => {
		const count = Number(simCount);
		const { min, max } = selectedLimits;

		if (!choosePool || count <= 0 || min <= 0 || max <= 0) {
			return false;
		}

		return count >= min && count <= max && count % 5 === 0;
	}, [choosePool, simCount, selectedLimits]);

	const invoice = useMemo(() => {
		const kitPrice = Number(choosePool?.kitPrice) || 0;
		const count = Number(simCount) || 0;
		const taxPercent = Number(tax) || 0;

		const subTotal = kitPrice * count;
		const taxAmount = Math.round((subTotal * taxPercent) / 100);
		const totalAmount = subTotal + taxAmount;

		return {
			kitPrice,
			count,
			subTotal,
			taxAmount,
			totalAmount,
		};
	}, [choosePool, simCount, tax]);

	// اسم نمایشی مخزن انتخاب‌شده (از لیست poolType ها) برای پیام زیر باکس «انتخاب مخزن»
	const selectedPoolName = pool.find((item) => Number(item.value) === Number(choosePool?.poolType))?.name || "";

	// مخزن «تحویل حضوری» (poolType === 1) نیازی به پرداخت نداره، فارغ از اینکه kitPrice چی برگردونده
	const isPickupPool = Number(choosePool?.poolType) === 1;
	const isFreePool = Boolean(choosePool) && isPickupPool;
	const shouldShowInvoice = Boolean(choosePool) && !isPickupPool && invoice.kitPrice > 0 && isCountValid;

	// خودِ ثبت درخواست؛ چه مخزن رایگان باشه (مستقیم صدا زده میشه) چه بعد از تایید تو مودال پرداخت
	const submitRequest = async () => {
		setModalState(null);
		setIsSubmitting(true);
		try {
			const payload = {
				pooltype: Number(choosePool?.poolType),
				requestedquantity: Number(simCount),
			};

			const response = await registerKitRequest(payload);

			if (response) {
				// اگه مخزن پولی بود، مبلغش از کیف پول بلوکه/کسر شده؛ باید موجودی رو به‌روز کنیم
				// طبق الگوی charge.jsx، اعمال شدنش تو بک‌اند کمی طول می‌کشه، پس قبل از خوندن موجودی جدید یه مکث لازمه
				if (!isFreePool) {
					await delay(3000);
					const updatedUser = await queryCurrentUser();
					if (updatedUser) {
						dispatch(globalValue({ _currentUser: { ..._currentUser, ...updatedUser } }));
					}
				}

				setModalState("success");
				form.resetFields();
				setChoosePool(null);
				setSimCount(0);
			}
		} catch (error) {
			console.error("Failed to register kit request:", error);
		}
		setIsSubmitting(false);
	};

	const handleSubmit = async () => {
		// مخزن رایگان: نه پرداختی هست نه چک موجودی لازمه، ولی همچنان باید تاییدیه گرفته بشه
		if (!isFreePool && walletAmount < invoice.totalAmount) {
			setModalState("error");
			return;
		}

		setModalState("confirm");
	};

	const handleCallBackCharge = (value) => {
		try {
			if (value?.length > 0) {
				bankRef.current.innerHTML = value;
				document.querySelector('form[action="https://asan.shaparak.ir"]').submit();
			} else {
				setOpenIncress(false);
			}
		} catch (error) {
			console.error(error);
		}
	};

	const modalContent = {
		confirm: {
			title: "درخواست کیت سیم‌کارت",
			description: isFreePool ? (
				"آیا عملیات فوق مورد تایید می‌باشد؟"
			) : (
				<>
					آیا عملیات فوق مورد تایید می‌باشد؟
					<br />
					مبلغ <span dir="ltr">{complexDigitToman(invoice.totalAmount)}</span> تومان در کیف پول شما بلوکه خواهد شد و پس از تایید این مبلغ از کیف
					پول شما کسر خواهد شد.
				</>
			),
			buttonText: "تایید",
			onAction: submitRequest,
		},
		success: {
			title: "درخواست کیت سیم‌کارت",
			description: "درخواست کیت شما با موفقیت ثبت شد و در انتظار تایید می‌باشد.",
			buttonText: "متوجه شدم",
			onAction: () => setModalState(null),
		},
		error: {
			title: "درخواست کیت سیم‌کارت",
			description: "موجودی کیف پول شما کافی نمی‌باشد جهت افزایش اعتبار اقدام نمایید",
			buttonText: "افزایش اعتبار",
			onAction: () => {
				setModalState(null);
				setOpenIncress(true);
			},
		},
	}[modalState];

	return (
		<>
			<Form form={form} id="frmService" name="frmService" layout="vertical" onFinish={handleSubmit}>
				<Row gutter={30} justify="start">
					<Col xxl={6} xl={8} lg={10} md={10} sm={12} xs={24}>
						<Form.Item
							className="input-normal"
							name="type"
							label="انتخاب مخزن"
							rules={[
								{
									required: true,
									message: "مخزن را انتخاب کنید.",
								},
							]}
							extra={
								selectedPoolName ? (
									<p className="extraNewRequestbulk">
										<InfoCircleOutlined />
										با خرید این نوع کیت تنها امکان استفاده از آن برای {selectedPoolName} و تنها با دسترسی شما وجود خواهد داشت.
									</p>
								) : null
							}>
							<Select placeholder="انتخاب مخزن" onChange={handlePoolChange} onClear={handlePoolClear} allowClear>
								{pool.map((type) => (
									<Select.Option key={type.value} value={type.value}>
										{type.name}
									</Select.Option>
								))}
							</Select>
						</Form.Item>
					</Col>
				</Row>

				<Row gutter={30} justify="start">
					<Col xxl={6} xl={8} lg={10} md={10} sm={12} xs={24}>
						<Form.Item
							className="input-normal"
							name="certno"
							dependencies={["type"]}
							rules={[
								validateInput({
									t,
									validate: true,
									type: "number",
								}),
								{
									validator: (_, value) => {
										if (!choosePool) {
											return Promise.reject(new Error("ابتدا مخزن را انتخاب کنید."));
										}

										if (value === undefined || value === null || value === "") {
											return Promise.reject(new Error("تعداد کیت درخواستی را وارد کنید."));
										}

										const count = Number(value);
										const { min, max } = selectedLimits;

										if (!Number.isFinite(count)) {
											return Promise.reject(new Error("تعداد واردشده معتبر نیست."));
										}

										if (count < min || count > max) {
											return Promise.reject(
												new Error(
													min === max
														? `تعداد کیت مجاز درخواستی برابر با ${min} عدد می‌باشد.`
														: `تعداد کیت باید بین ${min} تا ${max} باشد.`,
												),
											);
										}

										if (count % 5 !== 0) {
											return Promise.reject(new Error("تعداد کیت باید مضربی از ۵ باشد."));
										}

										return Promise.resolve();
									},
								},
							]}
							extra={
								choosePool && selectedLimits.min > 0 && selectedLimits.max > 0 ? (
									<>
										<p className="extraNewRequestbulk">
											<InfoCircleOutlined />
											{selectedLimits.min === selectedLimits.max
												? `تعداد کیت مجاز درخواستی برابر با ${selectedLimits.min} عدد می‌باشد.`
												: `تعداد مجاز برای این مخزن از ${selectedLimits.min} تا ${selectedLimits.max} کیت است.`}
										</p>

										<p className="extraNewRequestbulk">
											<InfoCircleOutlined />
											تعداد کیت درخواستی باید مضربی از ۵ باشد.
										</p>
									</>
								) : (
									<p className="extraNewRequestbulk">
										<InfoCircleOutlined />
										ابتدا مخزن موردنظر را انتخاب کنید.
									</p>
								)
							}>
							<Iinput placeholder="تعداد کیت درخواستی" dir="ltr" disabled={!choosePool} onChange={handleCountChange} />
						</Form.Item>
					</Col>
				</Row>

				{shouldShowInvoice && (
					<Row gutter={30} justify="start">
						<Col xxl={6} xl={8} lg={10} md={10} sm={12} xs={24}>
							<ul className="factor">
								<li>
									<div>مبلغ هر کیت</div>

									<div>
										<span>{complexDigitToman(invoice.kitPrice)}</span> تومان
									</div>
								</li>

								<li>
									<div>تعداد درخواستی</div>

									<div>
										<span>{invoice.count}</span> کیت
									</div>
								</li>

								<li>
									<div>مبلغ قبل از مالیات</div>

									<div>
										<span>{complexDigitToman(invoice.subTotal)}</span> تومان
									</div>
								</li>

								<li>
									<div>مالیات ارزش افزوده ({tax}٪)</div>

									<div>
										<span>{complexDigitToman(invoice.taxAmount)}</span> تومان
									</div>
								</li>

								<li>
									<div>مبلغ کل پرداختی</div>

									<div>
										<span>{complexDigitToman(invoice.totalAmount)}</span> تومان
									</div>
								</li>
							</ul>
						</Col>
					</Row>
				)}
				<Row gutter={30} justify="start">
					<Col xxl={6} xl={8} lg={10} md={10} sm={12} xs={24}>
						<div className="holder-button">
							<Button form="frmService" key="submit" htmlType="submit" className="btn-normal btn-aptel" disabled={isSubmitting}>
								{isFreePool ? "تایید نهایی سفارش" : "تایید و پرداخت"}
							</Button>
						</div>
					</Col>
				</Row>

				<Loading isOpen={isSubmitting} />
			</Form>

			<KitRequestModal
				open={Boolean(modalState)}
				state={modalState}
				title={modalContent?.title}
				description={modalContent?.description}
				buttonText={modalContent?.buttonText}
				loading={modalState === "confirm" && isSubmitting}
				onClose={() => setModalState(null)}
				onAction={modalContent?.onAction}
			/>

			<IncressCharge
				open={openIncress}
				planid={process.env.REACT_APP_PLANID_CHARGE_WALLET}
				msisdn={_currentUser?.telnoone}
				price={Math.max(invoice.totalAmount - walletAmount, 0)}
				callback={handleCallBackCharge}
			/>

			<div ref={bankRef}></div>
		</>
	);
};

export default NewRequest;
